import React from 'react';
import {
  Title2,
  Text,
  Button,
  Caption1,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  Input,
  Field,
  Tooltip,
  Badge,
  Checkbox,

  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Edit20Regular,
  Add20Regular,
  Dismiss12Regular,
  Delete20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { useEnvironment } from '../../contexts/AppContext';
import { ApisService, BackendsService, initializeTRAPIClient } from '../../services/api';
import { AliasesService } from '../../services/api/aliasesService';
import { createTmdsClient } from '../../services/tmds';
import { getErrorMessage } from '../../utils/errors';
import { ExpandableTable } from '../../components/ExpandableTable';
import type { Column } from '../../components/ExpandableTable';
import DetailsFlyout from '../../components/DetailsFlyout';
import { ApiForm } from '../../components/forms/ApiForm';
import type {
  APIObject,
  AliasObject,
  SecurityConfiguration,
  SecurityGroup,
} from '../../types/trapi';

type ApiItem = APIObject & {
  name?: string;
  displayName?: string;
  description?: string;
};

// DR-001: section-level patches never carry security.identities. Group edits may adjust
// security.groups only; /managemsi remains the single identity mutation boundary.
type ApiSectionPatch = Partial<Omit<ApiItem, 'security'>> & { security?: { groups: SecurityGroup[] } };

// Shape returned by /models — keyed by apiPath
type ModelsRecord = { id: string; [k: string]: unknown };
type ModelsPayload = Record<string, { object?: string; data?: ModelsRecord[] }>;

// Shape returned by /pathmappings (per-endpoint granularity)
type PathMappingDeployment = { Name?: string; name?: string; model?: string; [k: string]: unknown };
type PathMappingEndpoint = { name?: string; deployments?: PathMappingDeployment[] | PathMappingDeployment; [k: string]: unknown };
type PathMappingEntry = { apiPath?: string; endpoints?: PathMappingEndpoint[]; [k: string]: unknown };
type PathMappingsData = PathMappingEntry[];

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalM },
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, paddingBottom: tokens.spacingVerticalXS,
  },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  expandedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: tokens.spacingHorizontalL },
  tooltipContent: { display: 'flex', flexDirection: 'column', gap: '2px' },
  tooltipDivider: { borderBottom: `1px solid ${tokens.colorNeutralStroke1}`, margin: '2px 0' },
  flyoutItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px',
    border: `1px solid ${tokens.colorNeutralStroke3}`, borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
  },
  flyoutAddForm: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    paddingTop: tokens.spacingVerticalM, marginTop: tokens.spacingVerticalS,
  },
  addRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end' },
  endpointRow: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    alignItems: 'start',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  endpointName: {
    fontFamily: 'var(--fontFamilyMonospace, monospace)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: 600 as const,
    paddingTop: '2px',
  },
  modelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: `2px ${tokens.spacingHorizontalM}`,
  },
  modelName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: `2px ${tokens.spacingHorizontalM}`,
  },
  focusableButton: {
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusMedium,
    },
  },
});

/** Look up deployment names for an apiPath from /models data */
function getDeploymentsForApiPath(apiPath: string, data: ModelsPayload | undefined): string[] {
  if (!data || typeof data !== 'object') return [];
  const normPath = apiPath.replace(/^\//, '').toLowerCase();
  for (const [key, val] of Object.entries(data)) {
    if (key.replace(/^\//, '').toLowerCase() === normPath) {
      return (val?.data ?? []).map(d => d.id).filter(Boolean);
    }
  }
  return [];
}

/** Look up per-endpoint deployments from pathmappings data */
function getEndpointModels(endpointName: string, apiPath: string, data: PathMappingsData | undefined): string[] {
  if (!data || !Array.isArray(data)) return [];
  const normPath = apiPath.replace(/^\//, '').toLowerCase();
  const mapping = data.find(m => (m.apiPath ?? '').replace(/^\//, '').toLowerCase() === normPath);
  if (!mapping) return [];
  const normEp = endpointName.toLowerCase();
  for (const ep of mapping.endpoints ?? []) {
    if ((ep.name ?? '').toLowerCase() === normEp) {
      let deps = ep.deployments;
      if (!deps) return [];
      if (!Array.isArray(deps)) deps = [deps];
      return deps.map(d => d.Name || d.name || d.model || '(unknown)');
    }
  }
  return [];
}

// ── Collapsible single endpoint row (only when per-endpoint data available) ──
function EndpointItem({ name, models, styles }: {
  name: string; models: string[];
  styles: ReturnType<typeof useStyles>;
}) {
  const [open, setOpen] = React.useState(false);
  const hasModels = models.length > 0;
  return (
    <div style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `${tokens.spacingVerticalXS} 0`, cursor: hasModels ? 'pointer' : 'default', userSelect: 'none', outline: 'none' }}
        className={hasModels ? styles.focusableButton : undefined}
        onClick={() => hasModels && setOpen(p => !p)}
        role={hasModels ? 'button' : undefined}
        tabIndex={hasModels ? 0 : undefined}
        onKeyDown={e => { if (hasModels && (e.key === 'Enter' || e.key === ' ')) setOpen(p => !p); }}
      >
        {hasModels && <span style={{ fontSize: 10, width: 12, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>}
        <span className={styles.endpointName}>{name}</span>
        {hasModels && (
          <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {models.length} model{models.length !== 1 ? 's' : ''}
          </Caption1>
        )}
      </div>
      {open && hasModels && (
        <div className={styles.modelsGrid} style={{ paddingLeft: 20, paddingBottom: tokens.spacingVerticalS }}>
          {models.map(m => <span key={m} className={styles.modelName} title={m}>{m}</span>)}
        </div>
      )}
    </div>
  );
}

// ── Read-only Endpoints Section ──
// Per-endpoint model breakdown when pathmappings available;
// otherwise API-level models shown in a collapsible summary below the list.
function EndpointsSection({ api, modelsData, modelsLoading, modelsError, pathmappings, onEdit }: {
  api: ApiItem;
  modelsData: ModelsPayload | undefined;
  modelsLoading?: boolean;
  modelsError?: string;
  pathmappings?: PathMappingsData;
  onEdit: () => void;
}) {
  const styles = useStyles();
  const [apiModelsOpen, setApiModelsOpen] = React.useState(false);
  const sortedEndpoints = React.useMemo(
    () => [...(api.endpoints ?? [])].sort((a, b) => a.localeCompare(b)),
    [api.endpoints],
  );
  const hasPerEndpointData = !!pathmappings;

  const apiModels = React.useMemo(
    () => getDeploymentsForApiPath(api.apiPath, modelsData).sort((a, b) => a.localeCompare(b)),
    [api.apiPath, modelsData],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <Text weight="semibold">Endpoints ({sortedEndpoints.length})</Text>
          {apiModels.length > 0 && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              · {apiModels.length} model{apiModels.length !== 1 ? 's' : ''}
            </Caption1>
          )}
        </div>
        <Button size="small" appearance="subtle" icon={<Edit20Regular />}
          onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit endpoints">Edit</Button>
      </div>
      {modelsError && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>Models unavailable: {modelsError}</Caption1>}
      {sortedEndpoints.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sortedEndpoints.map(ep => {
            // Only show per-endpoint models when we actually have per-endpoint data
            const epModels = hasPerEndpointData
              ? getEndpointModels(ep, api.apiPath, pathmappings).sort((a, b) => a.localeCompare(b))
              : [];
            return (
              <EndpointItem key={ep} name={ep} models={epModels} styles={styles} />
            );
          })}
        </div>
      ) : <Caption1>None</Caption1>}

      {/* API-level models summary (when no per-endpoint data) */}
      {!hasPerEndpointData && apiModels.length > 0 && (
        <div style={{ marginTop: tokens.spacingVerticalXS }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', outline: 'none' }}
            className={styles.focusableButton}
            onClick={() => setApiModelsOpen(p => !p)}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setApiModelsOpen(p => !p); }}
          >
            <span style={{ fontSize: 10, width: 12 }}>{apiModelsOpen ? '▾' : '▸'}</span>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {apiModels.length} model{apiModels.length !== 1 ? 's' : ''} across all endpoints
            </Caption1>
          </div>
          {apiModelsOpen && (
            <div className={styles.modelsGrid} style={{ paddingLeft: 20, marginTop: tokens.spacingVerticalXS }}>
              {apiModels.map(m => <span key={m} className={styles.modelName} title={m}>{m}</span>)}
            </div>
          )}
        </div>
      )}
      {modelsLoading && apiModels.length === 0 && (
        <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }}>Loading models…</Caption1>
      )}
    </div>
  );
}

// ── Endpoints Flyout Content ──
function EndpointsFlyoutContent({ api, onSave, saving, onClose }: {
  api: ApiItem; onSave: (endpoints: string[]) => Promise<void>; saving: boolean; onClose: () => void;
}) {
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();

  const [selected, setSelected] = React.useState<Set<string>>(new Set(api.endpoints ?? []));
  const [backends, setBackends] = React.useState<string[]>([]);
  const [backendsLoading, setBackendsLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('');

  // Fetch available backends
  React.useEffect(() => {
    (async () => {
      try {
        if (!account) return;
        const token = await getToken();
        const list = await BackendsService.getBackends({ environment: currentEnvironment, accessToken: token });
        setBackends(
          (Array.isArray(list) ? list : [])
            .map(b => (b?.name ?? '').toString().trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b)),
        );
      } catch { setBackends([]); }
      finally { setBackendsLoading(false); }
    })();
  }, [account, getToken, currentEnvironment]);

  const filtered = React.useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return backends;
    return backends.filter(b => b.toLowerCase().includes(q));
  }, [backends, filter]);

  const toggle = (name: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const save = async () => { await onSave(Array.from(selected)); onClose(); };

  return (
    <>
      <Input
        size="small"
        placeholder="Filter backends…"
        value={filter}
        onChange={(_, d) => setFilter(d.value)}
        style={{ marginBottom: 8 }}
      />
      <Caption1 style={{ color: tokens.colorNeutralForeground3, marginBottom: 4 }}>
        {selected.size} selected · {backends.length} available
      </Caption1>
      <div style={{
        maxHeight: 400, overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke3}`,
        borderRadius: tokens.borderRadiusMedium, padding: 4,
      }}>
        {backendsLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12 }}>
            <Spinner size="tiny" /> <Text size={200}>Loading backends…</Text>
          </div>
        ) : filtered.length === 0 ? (
          <Caption1 style={{ padding: 12, display: 'block' }}>
            {filter ? 'No backends match filter' : 'No backends available'}
          </Caption1>
        ) : (
          filtered.map(name => (
            <Checkbox
              key={name}
              checked={selected.has(name)}
              onChange={() => toggle(name)}
              label={name}
              style={{ display: 'flex', width: '100%' }}
            />
          ))
        )}
      </div>

      <div style={{ position: 'sticky', bottom: 0, display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 12 }}>
        <Button appearance="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button appearance="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </>
  );
}

// ── Read-only Aliases Section ──
// Small inline alias count for table row
function AliasCount({ apiPath }: { apiPath: string }) {
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const q = useQuery({
    queryKey: ['aliases-count', apiPath],
    queryFn: async () => {
      if (!account) return 0;
      const token = await getToken();
      const result = await AliasesService.getAliases({ apiPath, environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? result.length : 0;
    },
    enabled: !!account,
    staleTime: 120_000,
  });
  if (q.isLoading) return <Text size={200}>…</Text>;
  return <>{q.data ?? 0}</>;
}

function AliasesSection({ api, onEdit }: { api: ApiItem; onEdit: () => void }) {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const q = useQuery<AliasObject[]>({
    queryKey: ['admin', 'aliases', currentEnvironment, api.apiPath],
    queryFn: async () => {
      const token = await getToken();
      const result = await AliasesService.getAliases({ apiPath: api.apiPath, environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? result : [];
    },
    enabled: !!account && !!api.apiPath,
    staleTime: 60_000,
  });
  const aliases = q.data ?? [];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <Text weight="semibold">Aliases ({q.isLoading ? '…' : aliases.length})</Text>
        </div>
        <Button size="small" appearance="subtle" icon={<Edit20Regular />}
          onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit aliases">Edit</Button>
      </div>
      {q.isLoading ? (
        <><Spinner size="tiny" /> <Text size={200}>Loading aliases…</Text></>
      ) : aliases.length === 0 ? (
        <Caption1>No aliases configured</Caption1>
      ) : (
        <div className={styles.itemsGrid}>
          {aliases.sort((a, b) => (a.aliasPath || '').localeCompare(b.aliasPath || '')).map(a => (
            <Caption1 key={a.id}><code>{a.aliasPath}</code></Caption1>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Aliases Flyout Content ──
function AliasesFlyoutContent({ api, onClose }: { api: ApiItem; onClose: () => void }) {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const qc = useQueryClient();

  const q = useQuery<AliasObject[]>({
    queryKey: ['admin', 'aliases', currentEnvironment, api.apiPath],
    queryFn: async () => {
      const token = await getToken();
      const result = await AliasesService.getAliases({ apiPath: api.apiPath, environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? result : [];
    },
    enabled: !!account && !!api.apiPath,
    staleTime: 60_000,
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      await AliasesService.deleteAlias({ id, environment: currentEnvironment, accessToken: token });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'aliases', currentEnvironment, api.apiPath] }); },
    onError: (error: unknown) => { console.error('Delete alias failed:', getErrorMessage(error)); },
  });

  const addMut = useMutation({
    mutationFn: async (aliasPath: string) => {
      const token = await getToken();
      return AliasesService.createAlias({ aliasPath, apiPath: api.apiPath, environment: currentEnvironment, accessToken: token });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'aliases', currentEnvironment, api.apiPath] }); },
    onError: (error: unknown) => { console.error('Add alias failed:', getErrorMessage(error)); },
  });

  const [newAlias, setNewAlias] = React.useState('');
  const aliases = q.data ?? [];

  const addAlias = () => {
    const v = newAlias.trim();
    if (v) { addMut.mutate(v); setNewAlias(''); }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {q.isLoading && <><Spinner size="tiny" /> <Text size={200}>Loading…</Text></>}
        {!q.isLoading && aliases.length === 0 && <Caption1>No aliases. Add one below.</Caption1>}
        {aliases.map(a => (
          <div key={a.id} className={styles.flyoutItem}>
            <Text>{a.aliasPath}</Text>
            <Button size="small" appearance="subtle" icon={<Dismiss12Regular />}
              onClick={() => deleteMut.mutate(a.id)} disabled={deleteMut.isPending} title="Remove alias" aria-label="Remove alias" />
          </div>
        ))}
      </div>

      <div className={styles.flyoutAddForm}>
        <Text weight="semibold" size={200}>Add alias</Text>
        <div className={styles.addRow}>
          <Field style={{ flex: 1 }} hint="e.g., gcr/myalias">
            <Input size="small" placeholder="Alias path" value={newAlias}
              onChange={(_, d) => setNewAlias(d.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }} />
          </Field>
          <Button size="small" appearance="outline" icon={<Add20Regular />}
            onClick={addAlias} disabled={!newAlias.trim() || addMut.isPending}>
            {addMut.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>

      <div style={{ position: 'sticky', bottom: 0, display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 12 }}>
        <Button appearance="secondary" onClick={onClose}>Close</Button>
      </div>
    </>
  );
}

// ── Read-only Security Groups Section ──
function GroupsSection({ api, onEdit }: { api: ApiItem; onEdit: () => void }) {
  const styles = useStyles();
  const security = api.security as SecurityConfiguration | undefined;
  const groups = React.useMemo(
    () => [...(security?.groups ?? [])].sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || '')),
    [security?.groups],
  );
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <Text weight="semibold">Security Groups ({groups.length})</Text>
        </div>
        <Button size="small" appearance="subtle" icon={<Edit20Regular />}
          onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit groups">Edit</Button>
      </div>
      {groups.length > 0 ? (
        <div className={styles.itemsGrid}>
          {groups.map((g, i) => (
            <Caption1 key={i} title={g.id}>{g.name || g.id || `Group ${i + 1}`}</Caption1>
          ))}
        </div>
      ) : <Caption1>No security groups</Caption1>}
    </div>
  );
}

// ── Groups Flyout Content ──
function GroupsFlyoutContent({ api, onSave, saving, onClose }: {
  api: ApiItem; onSave: (groups: SecurityGroup[]) => Promise<void>; saving: boolean; onClose: () => void;
}) {
  const styles = useStyles();
  const security = api.security as SecurityConfiguration | undefined;
  const [draft, setDraft] = React.useState<SecurityGroup[]>([...(security?.groups ?? [])]);
  const [editIdx, setEditIdx] = React.useState<number | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);

  const [formId, setFormId] = React.useState('');
  const [formName, setFormName] = React.useState('');
  const [formOwner, setFormOwner] = React.useState('');
  const [formStart, setFormStart] = React.useState('');
  const [formEnd, setFormEnd] = React.useState('');

  const clearForm = () => { setFormId(''); setFormName(''); setFormOwner(''); setFormStart(''); setFormEnd(''); setEditIdx(null); setFormOpen(false); };

  const openAdd = () => { clearForm(); setFormOpen(true); };
  const openEdit = (idx: number) => {
    const g = draft[idx];
    setFormId(g.id); setFormName(g.name || ''); setFormOwner(g.owner || ''); setFormStart(g.startDate || ''); setFormEnd(g.endDate || '');
    setEditIdx(idx); setFormOpen(true);
  };

  const remove = (idx: number) => { setDraft(prev => prev.filter((_, i) => i !== idx)); if (editIdx === idx) clearForm(); };

  const saveItem = () => {
    const id = formId.trim();
    if (!id) return;
    const item: SecurityGroup = { id, name: formName.trim(), owner: formOwner.trim() || undefined, startDate: formStart || undefined, endDate: formEnd || undefined };
    if (editIdx !== null) setDraft(prev => prev.map((g, i) => i === editIdx ? item : g));
    else setDraft(prev => [...prev, item]);
    clearForm();
  };

  const save = async () => { await onSave(draft); onClose(); };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Caption1>{draft.length} security group{draft.length === 1 ? '' : 's'}</Caption1>
        <Button size="small" appearance="primary" icon={<Add20Regular />} onClick={openAdd} disabled={formOpen}>Add</Button>
      </div>

      {draft.length > 0 && (
        <Table size="small" aria-label="Security Groups">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '35%' }}>Name</TableHeaderCell>
              <TableHeaderCell style={{ width: '25%' }}>Owner</TableHeaderCell>
              <TableHeaderCell style={{ width: '15%' }}>Expiry</TableHeaderCell>
              <TableHeaderCell style={{ width: '25%', textAlign: 'right' }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.map((g, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Text weight="semibold" size={200} style={{ display: 'block' }}>{g.name || '(unnamed)'}</Text>
                  <Caption1 style={{ fontFamily: 'monospace', color: tokens.colorNeutralForeground3 }} title={g.id}>
                    {g.id ? `${g.id.slice(0, 8)}…` : '-'}
                  </Caption1>
                </TableCell>
                <TableCell><Caption1>{g.owner || '-'}</Caption1></TableCell>
                <TableCell><Caption1>{g.endDate || '-'}</Caption1></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button size="small" appearance="subtle" icon={<Edit20Regular />} onClick={() => openEdit(i)}>Edit</Button>
                    <Button size="small" appearance="outline" icon={<Delete20Regular />}
                      style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
                      onClick={() => remove(i)}>Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {formOpen && (
        <div className={styles.flyoutAddForm}>
          <Text weight="semibold" size={200}>{editIdx !== null ? 'Edit group' : 'Add group'}</Text>
          <Field label="Group ID" required><Input size="small" value={formId} onChange={(_, d) => setFormId(d.value)} disabled={editIdx !== null} /></Field>
          <Field label="Name"><Input size="small" value={formName} onChange={(_, d) => setFormName(d.value)} /></Field>
          <Field label="Owner"><Input size="small" value={formOwner} onChange={(_, d) => setFormOwner(d.value)} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Start Date"><Input size="small" type="date" value={formStart} onChange={(_, d) => setFormStart(d.value)} /></Field>
            <Field label="End Date"><Input size="small" type="date" value={formEnd} onChange={(_, d) => setFormEnd(d.value)} /></Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            <Button size="small" appearance="secondary" onClick={clearForm}>Cancel</Button>
            <Button size="small" appearance="primary" onClick={saveItem} disabled={!formId.trim()}>
              {editIdx !== null ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 12 }}>
        <Button appearance="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button appearance="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </>
  );
}

// ── Read-only Managed Identities Section ──
function IdentitiesSection({ api }: { api: ApiItem }) {
  const styles = useStyles();
  const security = api.security as SecurityConfiguration | undefined;
  const identities = React.useMemo(
    () => [...(security?.identities ?? [])].sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || '')),
    [security?.identities],
  );
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <Text weight="semibold">Managed Identities ({identities.length})</Text>
        </div>
      </div>
      <Caption1>Use Manage MSI to add, update, or remove identities. Use Models Access to manage access.</Caption1>
      {identities.length > 0 ? (
        <div className={styles.itemsGrid}>
          {identities.map((mi, i) => (
            <Caption1 key={i}>{mi.name || `Identity ${i + 1}`}</Caption1>
          ))}
        </div>
      ) : <Caption1>No managed identities</Caption1>}
    </div>
  );
}

export default function AdminApis() {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const qc = useQueryClient();

  React.useEffect(() => { initializeTRAPIClient({ environment: currentEnvironment }); }, [currentEnvironment]);

  const tmds = React.useMemo(() => createTmdsClient(getToken), [getToken]);

  // Data queries
  const query = useQuery<ApiItem[]>({
    queryKey: ['admin', 'apis', currentEnvironment],
    queryFn: async () => {
      const token = await getToken();
      const result = await ApisService.getApis({ environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? (result as ApiItem[]) : [];
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const modelsQuery = useQuery<ModelsPayload>({
    queryKey: ['models'],
    queryFn: () => tmds.getModels<ModelsPayload>(),
    enabled: !!account,
    staleTime: 120_000,
  });

  // Per-endpoint model mapping (pathmappings)
  const pathmappingsQuery = useQuery<PathMappingsData>({
    queryKey: ['pathmappings'],
    queryFn: () => tmds.getPathMappings<PathMappingsData>(),
    enabled: !!account,
    staleTime: 120_000,
    retry: 1,
  });

  const allItems = query.data ?? [];
  const existingPaths = React.useMemo(() => allItems.map(a => a.apiPath), [allItems]);

  // Flyout state — create API
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  const [flyoutMode, setFlyoutMode] = React.useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = React.useState<ApiItem | null>(null);

  // Section edit flyout state — endpoints/aliases/groups are editable; identities are
  // read-only (managed exclusively via /managemsi), so there is no 'identities' flyout.
  const [editFlyout, setEditFlyout] = React.useState<{ type: 'endpoints' | 'aliases' | 'groups'; api: ApiItem } | null>(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [formStatus, setFormStatus] = React.useState<{ changeCount: number; changes: string[]; issues: string[]; canSubmit: boolean }>({ changeCount: 0, changes: [], issues: [], canSubmit: false });
  const [deleteTarget, setDeleteTarget] = React.useState<ApiItem | null>(null);

  // Mutations
  const createMut = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const token = await getToken();
      if (!payload['ci']) payload['ci'] = 'API';
      return ApisService.createApi({ environment: currentEnvironment, accessToken: token, data: payload });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'apis', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['apis', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Create API failed:', getErrorMessage(error)); },
  });

  const updateMut = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const token = await getToken();
      const id = typeof payload['id'] === 'string' ? payload['id'] : '';
      if (!id) throw new Error('Missing id for update');
      if (!payload['ci']) payload['ci'] = editItem?.ci ?? 'API';
      return ApisService.updateApi({ environment: currentEnvironment, accessToken: token, id, data: payload });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'apis', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['apis', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Update API failed:', getErrorMessage(error)); },
  });

  // Section-level update: merges partial update into the full API object
  const sectionUpdateMut = useMutation({
    mutationFn: async ({ api, patch }: { api: ApiItem; patch: ApiSectionPatch }) => {
      const token = await getToken();
      const id = api.id;
      if (!id) throw new Error('Missing id for update');
      const { security: patchSecurity, ...restPatch } = patch;
      const payload: Record<string, unknown> = { ...api, ...restPatch, ci: api.ci ?? 'API' };
      // DR-001: /managemsi is the single identity mutation boundary. The generic config
      // route must never transport security.identities; the server preserves stored
      // canonical identities server-side. Carry forward only the (optionally edited) groups.
      const groups = patchSecurity ? patchSecurity.groups : api.security?.groups ?? [];
      payload.security = { groups };
      return ApisService.updateApi({ environment: currentEnvironment, accessToken: token, id, data: payload });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'apis', currentEnvironment] });
    },
    onError: (error: unknown) => { console.error('Section update failed:', getErrorMessage(error)); },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      const apiToDelete = await ApisService.getApi({ id, environment: currentEnvironment, accessToken: token });
      if (apiToDelete?.apiPath) {
        const aliases = await AliasesService.getAliases({ apiPath: apiToDelete.apiPath, environment: currentEnvironment, accessToken: token });
        if (Array.isArray(aliases)) {
          for (const alias of aliases as AliasObject[]) {
            await AliasesService.deleteAlias({ id: alias.id, environment: currentEnvironment, accessToken: token });
          }
        }
      }
      return ApisService.deleteApi({ environment: currentEnvironment, accessToken: token, id });
    },
    onSuccess: async () => {
      setDeleteOpen(false);
      setDeleteTarget(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'apis', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['admin', 'aliases', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Delete API failed:', getErrorMessage(error)); },
  });

  const isFormLoading = createMut.isPending || updateMut.isPending;

  const handleFormSubmit = async (payload: any) => {
    const body = { ...payload } as Record<string, unknown>;
    if (flyoutMode === 'create') {
      await createMut.mutateAsync(body);
    } else {
      await updateMut.mutateAsync(body);
    }
  };

  const openCreate = () => { setEditItem(null); setFlyoutMode('create'); setFlyoutOpen(true); };
  const openEditApi = (api: ApiItem) => { setEditItem(api); setFlyoutMode('edit'); setFlyoutOpen(true); };
  const openDelete = (api: ApiItem) => { setDeleteTarget(api); setDeleteOpen(true); };

  // Section save handlers
  const handleEndpointsSave = async (api: ApiItem, endpoints: string[]) => {
    await sectionUpdateMut.mutateAsync({ api, patch: { endpoints } });
  };
  const handleGroupsSave = async (api: ApiItem, groups: SecurityGroup[]) => {
    // DR-001: send only the edited groups. Never transport security.identities from the
    // generic API editor; the server preserves stored canonical identities server-side.
    await sectionUpdateMut.mutateAsync({ api, patch: { security: { groups } } });
  };

  // Table columns
  const columns: Column<ApiItem>[] = React.useMemo(() => [
    { key: 'apiPath', label: 'API Path', render: (item) => <Text weight="semibold">{item.apiPath || '(unnamed)'}</Text>, getValue: (item) => item.apiPath || '', sortable: true },
    { key: 'apiFormat', label: 'Format', render: (item) => item.apiFormat || '-', getValue: (item) => item.apiFormat || '', sortable: true, filterable: true },
    { key: 'endpoints', label: 'Endpoints', render: (item) => String(item.endpoints?.length ?? 0), getValue: (item) => String(item.endpoints?.length ?? 0), sortable: true, hideOnMobile: true },
    { key: 'groups', label: 'Groups', render: (item) => String((item.security as SecurityConfiguration)?.groups?.length ?? 0), getValue: (item) => String((item.security as SecurityConfiguration)?.groups?.length ?? 0), sortable: true, hideOnMobile: true },
    { key: 'identities', label: 'Identities', render: (item) => String((item.security as SecurityConfiguration)?.identities?.length ?? 0), getValue: (item) => String((item.security as SecurityConfiguration)?.identities?.length ?? 0), sortable: true, hideOnMobile: true },
    { key: 'aliases', label: 'Aliases', render: (item) => <AliasCount apiPath={item.apiPath} />, getValue: () => '', sortable: false, hideOnMobile: true },
  ], []);

  const renderExpanded = (api: ApiItem) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
          <Button size="small" appearance="subtle" icon={<Edit20Regular />}
            onClick={(e) => { e.stopPropagation(); openEditApi(api); }}>Edit API</Button>
          <Button size="small" appearance="outline" icon={<Delete20Regular />}
            style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
            onClick={(e) => { e.stopPropagation(); openDelete(api); }}>Delete API</Button>
        </div>
        {api.description && <Caption1>{api.description}</Caption1>}

        <div className={styles.expandedGrid}>
          <div style={{ gridColumn: '1 / -1' }}>
            <EndpointsSection
              api={api}
              modelsData={modelsQuery.data}
              modelsLoading={modelsQuery.isLoading}
              modelsError={modelsQuery.isError ? (modelsQuery.error as Error)?.message : undefined}
              pathmappings={pathmappingsQuery.data}
              onEdit={() => setEditFlyout({ type: 'endpoints', api })}
            />
          </div>

          <AliasesSection
            api={api}
            onEdit={() => setEditFlyout({ type: 'aliases', api })}
          />

          <GroupsSection
            api={api}
            onEdit={() => setEditFlyout({ type: 'groups', api })}
          />

          <IdentitiesSection
            api={api}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={styles.header}>
        <Title2>APIs</Title2>
        <Button appearance="primary" onClick={openCreate}>New API</Button>
      </div>

      {query.error ? (
        <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{getErrorMessage(query.error)}</Text>
      ) : (
        <ExpandableTable<ApiItem>
          columns={columns}
          items={allItems}
          getRowKey={(item) => item.id || item.apiPath}
          renderExpanded={renderExpanded}
          loading={query.isLoading}
          emptyMessage="No APIs configured."
          searchPlaceholder="Search APIs…"
          defaultSortKey="apiPath"
        />
      )}

      {/* Create API Flyout */}
      <DetailsFlyout
        open={flyoutOpen}
        onClose={() => setFlyoutOpen(false)}
        title={flyoutMode === 'create' ? 'Create API' : 'Edit API'}
        size="large"
        footer={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
            <Button appearance="secondary" onClick={() => setFlyoutOpen(false)} disabled={isFormLoading}>Cancel</Button>
            <Button appearance="primary" type="submit" form="api-edit-form" disabled={isFormLoading || !formStatus.canSubmit}>
              {isFormLoading ? 'Saving…' : flyoutMode === 'create' ? 'Create API' : 'Update API'}
            </Button>
            {(formStatus.changeCount > 0 || formStatus.issues.length > 0) && (
              <Tooltip
                content={
                  <div>
                    {formStatus.changes.length > 0 && <div><strong>Changes:</strong> {formStatus.changes.join(', ')}</div>}
                    {formStatus.issues.length > 0 && <div style={{ color: tokens.colorPaletteRedForeground1 }}><strong>Issues:</strong> {formStatus.issues.join(', ')}</div>}
                  </div>
                }
                relationship="description"
              >
                <Badge appearance="filled" color={formStatus.issues.length > 0 ? 'danger' : 'informative'} size="small">
                  {formStatus.changeCount} change{formStatus.changeCount === 1 ? '' : 's'}{formStatus.issues.length > 0 ? `, ${formStatus.issues.length} issue${formStatus.issues.length === 1 ? '' : 's'}` : ''}
                </Badge>
              </Tooltip>
            )}
          </div>
        }
      >
        {(createMut.isError || updateMut.isError) && (
          <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginBottom: 8 }}>
            {((createMut.error || updateMut.error) as Error)?.message}
          </Text>
        )}
        <ApiForm
          formId="api-edit-form"
          hideActions
          mode={flyoutMode}
          initialData={editItem || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => setFlyoutOpen(false)}
          isLoading={isFormLoading}
          existingApiPaths={existingPaths}
          onStatusChange={setFormStatus}
        />
      </DetailsFlyout>

      {/* Edit Endpoints Flyout */}
      <DetailsFlyout
        open={editFlyout?.type === 'endpoints'}
        onClose={() => setEditFlyout(null)}
        title={`Edit Endpoints — ${editFlyout?.api.apiPath ?? ''}`}
      >
        {editFlyout?.type === 'endpoints' && (
          <EndpointsFlyoutContent
            api={editFlyout.api}
            onSave={(endpoints) => handleEndpointsSave(editFlyout.api, endpoints)}
            saving={sectionUpdateMut.isPending}
            onClose={() => setEditFlyout(null)}
          />
        )}
      </DetailsFlyout>

      {/* Edit Aliases Flyout */}
      <DetailsFlyout
        open={editFlyout?.type === 'aliases'}
        onClose={() => setEditFlyout(null)}
        title={`Edit Aliases — ${editFlyout?.api.apiPath ?? ''}`}
      >
        {editFlyout?.type === 'aliases' && (
          <AliasesFlyoutContent
            api={editFlyout.api}
            onClose={() => setEditFlyout(null)}
          />
        )}
      </DetailsFlyout>

      {/* Edit Security Groups Flyout */}
      <DetailsFlyout
        open={editFlyout?.type === 'groups'}
        onClose={() => setEditFlyout(null)}
        title={`Edit Security Groups — ${editFlyout?.api.apiPath ?? ''}`}
      >
        {editFlyout?.type === 'groups' && (
          <GroupsFlyoutContent
            api={editFlyout.api}
            onSave={(groups) => handleGroupsSave(editFlyout.api, groups)}
            saving={sectionUpdateMut.isPending}
            onClose={() => setEditFlyout(null)}
          />
        )}
      </DetailsFlyout>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={(_, data) => setDeleteOpen(!!data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Delete</DialogTitle>
            <div style={{ display: 'grid', gap: 6 }}>
              <Text>Delete API:</Text>
              <Text weight="semibold">{deleteTarget?.apiPath || '(no path)'}</Text>
              {deleteTarget?.description && <Caption1>{deleteTarget.description}</Caption1>}
              <Text>This will also delete all associated aliases. This action cannot be undone.</Text>
            </div>
            {deleteMut.isError && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{getErrorMessage(deleteMut.error)}</Text>}
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)} disabled={deleteMut.isPending}>Cancel</Button>
              <Button appearance="primary" onClick={() => deleteTarget?.id && deleteMut.mutate(deleteTarget.id)} disabled={deleteMut.isPending || !deleteTarget?.id}>
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
